import { loginApi, logoutApi, registerApi, setAuthHeader, verifyOtpApi } from '@/apis/auth.api';
import { clearAllWorkspaceStorage } from '@/lib/noteWindows/storage';
import { http } from '@/services/http.service';
import type { ICurrentUser, IUserLogin, IRegisterUser, ITwoFactorLoginResult } from '@/types/auth';

class AuthService {
  async login(user: IUserLogin): Promise<ICurrentUser | ITwoFactorLoginResult | null> {
    const response = await loginApi(user);
    const data = response.data as ICurrentUser & {
      two_factor_required?: boolean;
      pre_auth_token?: string;
    };

    // Server returned 202 — 2FA required, JWT in header is already revoked
    if (data.two_factor_required === true && data.pre_auth_token) {
      return { twoFactorRequired: true, preAuthToken: data.pre_auth_token };
    }

    if (response.headers.authorization) {
      const token = response.headers.authorization as string;
      localStorage.setItem('user', JSON.stringify(data));
      localStorage.setItem('token', token);
      setAuthHeader(token);
    }
    return data;
  }

  async loginWithOtp(preAuthToken: string, otpCode: string): Promise<ICurrentUser> {
    const response = await verifyOtpApi({ pre_auth_token: preAuthToken, otp_code: otpCode });
    const data = response.data as ICurrentUser;
    if (response.headers.authorization) {
      const token = response.headers.authorization as string;
      localStorage.setItem('user', JSON.stringify(data));
      localStorage.setItem('token', token);
      setAuthHeader(token);
    }
    return data;
  }

  async logout() {
    try {
      await logoutApi();
    } finally {
      this.clearCache();
      delete http.defaults.headers.common['Authorization'];
    }
  }

  async register(user: IRegisterUser) {
    return registerApi(user);
  }

  getUser(): ICurrentUser | null {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    try {
      return JSON.parse(raw) as ICurrentUser;
    } catch {
      return null;
    }
  }

  getToken() {
    return localStorage.getItem('token');
  }

  clearCache() {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    // Note windows and workspace layouts cache note titles and content
    // snippets — they must not outlive the session on a shared browser.
    clearAllWorkspaceStorage();
  }

  clearCacheAndRedirect() {
    this.clearCache();
    if (!window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
  }
}

export default new AuthService();
